// client/src/components/pages/LexiStar/Billing/SubscriptionPanel.tsx
//
// "Trucks on the subscription" on the Usage & billing page (ST-00241): every truck of the
// organisation with where it stands today — no GPS device linked, waiting for the first valid
// position, billed every month (from its activation date), notice received (billed up to and
// including the last month before its billing end date), no longer billed — and the trucks
// that were removed in the app or had their GPS device unlinked WITHOUT a written notice: they
// are still billed, so they are listed first, so nobody is billed by surprise.
//
// Global admins (GET /subscription answers can_manage — lexistar.billing.manage, the key that
// sets the plan) record a written removal notice (the date it arrived, a reference, a note;
// billing ends one calendar month later) and withdraw a live one before its billing end date,
// with a reason. Both are audited by the server; tenant users read the list only.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaExclamationTriangle, FaFileSignature, FaUndo } from 'react-icons/fa';
import UnifiedDatePicker from '../../../common/UnifiedDatePicker';
import { cardClasses, SkeletonBlock } from '../Dashboard/analyticsAtoms';
import { billingErrorCode, billingErrorText, getSubscription, recordNotice, withdrawNotice } from '../../../../services/lexistarBilling';
import type { SubscriptionAsset, SubscriptionResponse } from '../../../../types/lexistar-billing';
import {
  billingEndsOn,
  canWithdraw,
  dateLabel,
  lastChargedMonth,
  monthLabel,
  REMOVED_LABELS,
  subscriptionStatusText,
  validateNoticeDraft,
  type NoticeDraft,
} from './billingLogic';

const inputClass =
  'w-full px-2.5 py-2 border border-slate-300 rounded-md bg-surface text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
const smallButton =
  'inline-flex items-center gap-1.5 min-h-[36px] px-2.5 py-1.5 text-xs font-medium text-slate-700 bg-surface border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed';
const primary =
  'inline-flex items-center justify-center gap-1.5 min-h-[40px] px-3 py-2 text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed';
const button =
  'inline-flex items-center justify-center gap-1.5 min-h-[40px] px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50';

/** 'YYYY-MM-DD' <-> the picker's local Date (noon, so no time zone moves the day). */
const toDate = (day: string): Date | null => (/^\d{4}-\d{2}-\d{2}$/.test(day) ? new Date(`${day}T12:00:00`) : null);
const fromDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const STATUS_TONE: Record<SubscriptionAsset['status'], string> = {
  no_device: 'bg-slate-100 text-slate-700 border-slate-200',
  awaiting_fix: 'bg-slate-100 text-slate-700 border-slate-200',
  billable: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  notice_given: 'bg-amber-50 text-amber-800 border-amber-200',
  ended: 'bg-slate-100 text-slate-600 border-slate-200',
};

const truckName = (a: Pick<SubscriptionAsset, 'registration' | 'asset_id'>): string => a.registration || a.asset_id.slice(0, 8);

type Editing = { kind: 'record' | 'withdraw'; assetId: string } | null;

export default function SubscriptionPanel({ tenantId, reloadKey = 0 }: { tenantId: number | null; reloadKey?: number }) {
  const [data, setData] = useState<SubscriptionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [editing, setEditing] = useState<Editing>(null);
  const [tick, setTick] = useState(0);

  const load = useCallback(() => {
    let alive = true;
    getSubscription(tenantId)
      .then((r) => {
        if (!alive) return;
        setData(r);
        setError(null);
      })
      .catch((err) => {
        if (!alive) return;
        setData(null);
        setError(billingErrorText(err, 'The trucks on the subscription could not be loaded.'));
      });
    return () => {
      alive = false;
    };
  }, [tenantId]);

  useEffect(() => load(), [load, reloadKey, tick]);
  useEffect(() => setEditing(null), [tenantId]);

  const unnoticed = useMemo(() => (data ? data.assets.filter((a) => a.removed_without_notice) : []), [data]);

  const saved = (text: string) => {
    setEditing(null);
    setError(null);
    setNotice(text);
    setTick((k) => k + 1);
  };

  return (
    <section className={`${cardClasses} p-4 space-y-3`} aria-labelledby="billing-subscription-title">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="billing-subscription-title" className="text-sm font-semibold text-slate-900">
          Trucks on the subscription
        </h2>
        {data && (
          <p className="text-[11px] text-slate-500">
            As of {dateLabel(data.today)} · {data.billing_basis === 'flat_monthly' ? 'billed at a flat rate per truck per month' : 'billed per active truck (truck-days)'}
          </p>
        )}
      </div>
      <p className="text-xs text-slate-600">
        A truck is billed from its activation date — the first day its linked GPS device sent a valid position — until one calendar month after a
        written removal notice. Removing a truck in the app or unlinking its device does not stop billing.
      </p>

      {error && (
        <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
          {notice}
        </p>
      )}

      {unnoticed.length > 0 && (
        <div role="note" data-testid="subscription-unnoticed" className="flex gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
          <FaExclamationTriangle className="mt-0.5 shrink-0" size={13} aria-hidden="true" />
          <span>
            <strong>
              {unnoticed.length === 1 ? '1 truck was' : `${unnoticed.length} trucks were`} removed in the app, or lost {unnoticed.length === 1 ? 'its' : 'their'}{' '}
              GPS device, without a written notice
            </strong>{' '}
            — {unnoticed.length === 1 ? 'it is' : 'they are'} still billed every month: {unnoticed.map(truckName).join(', ')}.{' '}
            {data?.can_manage ? 'Record the notice when it arrives.' : 'Send Lex-Adapt the written notice to stop billing.'}
          </span>
        </div>
      )}

      {!data && !error ? (
        <SkeletonBlock className="h-32" />
      ) : data && data.assets.length === 0 ? (
        <p className="text-sm text-slate-600">There are no trucks in the asset register yet.</p>
      ) : data ? (
        <div className="overflow-x-auto -mx-1">
          <table className="min-w-full text-sm" aria-label="Trucks on the subscription">
            <thead>
              <tr className="text-left text-[11px] font-medium text-slate-500 border-b border-slate-200">
                <th scope="col" className="px-1 py-2">
                  Truck
                </th>
                <th scope="col" className="px-1 py-2">
                  Where it stands
                </th>
                <th scope="col" className="px-1 py-2">
                  Active since
                </th>
                <th scope="col" className="px-1 py-2">
                  Written notice
                </th>
                {data.can_manage && (
                  <th scope="col" className="px-1 py-2">
                    <span className="sr-only">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {[...data.assets]
                .sort((a, b) => Number(b.removed_without_notice) - Number(a.removed_without_notice))
                .map((a) => (
                  <Row
                    key={a.asset_id}
                    asset={a}
                    today={data.today}
                    canManage={data.can_manage}
                    tenantId={data.tenant_id}
                    editing={editing && editing.assetId === a.asset_id ? editing.kind : null}
                    onEdit={(kind) => {
                      setNotice(null);
                      setEditing(kind ? { kind, assetId: a.asset_id } : null);
                    }}
                    onSaved={saved}
                  />
                ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function Row({
  asset: a,
  today,
  canManage,
  tenantId,
  editing,
  onEdit,
  onSaved,
}: {
  asset: SubscriptionAsset;
  today: string;
  canManage: boolean;
  tenantId: number;
  editing: 'record' | 'withdraw' | null;
  onEdit: (kind: 'record' | 'withdraw' | null) => void;
  onSaved: (text: string) => void;
}) {
  const withdrawn = a.notices.filter((n) => n.withdrawn_at);
  const mayRecord = canManage && !a.notice && a.status !== 'ended';
  const mayWithdraw = canManage && canWithdraw(a.notice, today);
  const cols = canManage ? 5 : 4;
  return (
    <>
      <tr className="border-b border-slate-100 align-top" data-testid={`subscription-${a.asset_id}`}>
        <td className="px-1 py-2 font-medium text-slate-900">
          {truckName(a)}
          {a.fleet_no && <span className="block text-[11px] font-normal text-slate-500">fleet no. {a.fleet_no}</span>}
          {a.removed_without_notice && a.removed && (
            <span className="mt-0.5 inline-block rounded border border-amber-300 bg-amber-50 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
              {REMOVED_LABELS[a.removed]}
            </span>
          )}
        </td>
        <td className="px-1 py-2">
          <span className={`inline-block rounded-md border px-1.5 py-0.5 text-xs ${STATUS_TONE[a.status]}`}>{subscriptionStatusText(a)}</span>
        </td>
        <td className="px-1 py-2 text-slate-700 tabular-nums">{a.activated_on ? dateLabel(a.activated_on) : '—'}</td>
        <td className="px-1 py-2 text-slate-700">
          {a.notice ? (
            <>
              Received {dateLabel(a.notice.notice_received_on)}
              <span className="block text-[11px] text-slate-500">
                billing ends {dateLabel(a.notice.billing_ends_on)}
                {a.notice.reference ? ` · ${a.notice.reference}` : ''}
              </span>
            </>
          ) : (
            <span className="text-slate-400">—</span>
          )}
          {withdrawn.length > 0 && (
            <span
              className="block text-[11px] text-slate-500"
              title={withdrawn.map((n) => `${dateLabel(n.notice_received_on)}: withdrawn by ${n.withdrawn_by} — ${n.withdrawal_reason}`).join('\n')}
            >
              {withdrawn.length === 1 ? '1 withdrawn notice' : `${withdrawn.length} withdrawn notices`}
            </span>
          )}
        </td>
        {canManage && (
          <td className="px-1 py-2 text-right whitespace-nowrap">
            {mayRecord && (
              <button type="button" className={smallButton} onClick={() => onEdit(editing === 'record' ? null : 'record')} aria-expanded={editing === 'record'}>
                <FaFileSignature size={11} aria-hidden="true" /> Record notice…
              </button>
            )}
            {mayWithdraw && (
              <button type="button" className={smallButton} onClick={() => onEdit(editing === 'withdraw' ? null : 'withdraw')} aria-expanded={editing === 'withdraw'}>
                <FaUndo size={10} aria-hidden="true" /> Withdraw notice…
              </button>
            )}
          </td>
        )}
      </tr>
      {editing && (
        <tr className="border-b border-slate-100">
          <td colSpan={cols} className="px-1 pb-3">
            {editing === 'record' ? (
              <NoticeForm asset={a} today={today} tenantId={tenantId} onCancel={() => onEdit(null)} onSaved={onSaved} />
            ) : (
              <WithdrawForm asset={a} onCancel={() => onEdit(null)} onSaved={onSaved} />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function NoticeForm({
  asset,
  today,
  tenantId,
  onCancel,
  onSaved,
}: {
  asset: SubscriptionAsset;
  today: string;
  tenantId: number;
  onCancel: () => void;
  onSaved: (text: string) => void;
}) {
  const [draft, setDraft] = useState<NoticeDraft>({ notice_received_on: today, reference: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = (k: string) => `notice-${asset.asset_id}-${k}`;
  const checked = validateNoticeDraft(draft, today);
  const ends = checked.ok ? billingEndsOn(checked.value.notice_received_on) : null;

  const save = async () => {
    if (!checked.ok) {
      setError(checked.error);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const n = await recordNotice(asset.asset_id, { ...checked.value, tenant_id: tenantId });
      onSaved(
        `Notice recorded for ${truckName(asset)}: billing ends ${dateLabel(n.billing_ends_on)}, so ${monthLabel(lastChargedMonth(n.billing_ends_on))} is the last month charged. The draft statement follows at its next rebuild.`
      );
    } catch (err) {
      setError(
        billingErrorCode(err) === 'notice_exists'
          ? 'A notice for this truck was recorded meanwhile. Reload the page to see it.'
          : billingErrorText(err, 'The notice could not be recorded.')
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      aria-label={`Record a removal notice for ${truckName(asset)}`}
      className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div>
        <label htmlFor={id('date')} className="block text-[11px] font-medium text-slate-600 mb-1">
          Date the written notice arrived
        </label>
        <UnifiedDatePicker
          id={id('date')}
          value={toDate(draft.notice_received_on)}
          onChange={(d: Date | null) => setDraft((x) => ({ ...x, notice_received_on: d ? fromDate(d) : '' }))}
          maxDate={toDate(today)}
          bounds="past"
          dateFormat="yyyy/MM/dd"
          className={inputClass}
          autoComplete="off"
          title="Date the written notice arrived"
        />
      </div>
      <div>
        <label htmlFor={id('reference')} className="block text-[11px] font-medium text-slate-600 mb-1">
          Reference (optional)
        </label>
        <input
          id={id('reference')}
          value={draft.reference}
          maxLength={200}
          onChange={(e) => setDraft((x) => ({ ...x, reference: e.target.value }))}
          className={inputClass}
          placeholder="e.g. the letter or e-mail subject"
          autoComplete="off"
        />
      </div>
      <div>
        <label htmlFor={id('note')} className="block text-[11px] font-medium text-slate-600 mb-1">
          Note (optional)
        </label>
        <input id={id('note')} value={draft.note} maxLength={2000} onChange={(e) => setDraft((x) => ({ ...x, note: e.target.value }))} className={inputClass} autoComplete="off" />
      </div>
      <p className="sm:col-span-3 text-xs text-slate-600" data-testid="notice-preview">
        {ends
          ? `Billing ends ${dateLabel(ends)}: ${monthLabel(lastChargedMonth(ends))} is the last month ${truckName(asset)} is charged, in full.`
          : 'Choose the date the written notice arrived.'}
      </p>
      {error && (
        <p role="alert" className="sm:col-span-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}
      <div className="sm:col-span-3 flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={busy}>
          <FaFileSignature size={11} aria-hidden="true" /> {busy ? 'Recording…' : 'Record notice'}
        </button>
        <button type="button" className={button} onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function WithdrawForm({ asset, onCancel, onSaved }: { asset: SubscriptionAsset; onCancel: () => void; onSaved: (text: string) => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = `withdraw-${asset.asset_id}`;

  const save = async () => {
    if (!asset.notice) return;
    if (reason.trim().length < 3) {
      setError('Write why the notice is withdrawn (a few words).');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await withdrawNotice(asset.notice.id, reason.trim());
      onSaved(`The notice for ${truckName(asset)} was withdrawn: the truck is billed every month again.`);
    } catch (err) {
      const code = billingErrorCode(err);
      setError(
        code === 'notice_ended'
          ? 'Billing for this truck has already ended: the notice can no longer be withdrawn.'
          : code === 'already_withdrawn'
            ? 'This notice was already withdrawn. Reload the page.'
            : billingErrorText(err, 'The notice could not be withdrawn.')
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      aria-label={`Withdraw the removal notice for ${truckName(asset)}`}
      className="mt-1 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <label htmlFor={id} className="block text-[11px] font-medium text-slate-600">
        Why is the notice withdrawn?
      </label>
      <textarea id={id} rows={2} value={reason} maxLength={2000} onChange={(e) => setReason(e.target.value)} className={inputClass} />
      <p className="text-xs text-slate-600">The truck is billed every month again. The withdrawn notice stays on record with your reason.</p>
      {error && (
        <p role="alert" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <button type="submit" className={primary} disabled={busy}>
          <FaUndo size={10} aria-hidden="true" /> {busy ? 'Withdrawing…' : 'Withdraw notice'}
        </button>
        <button type="button" className={button} onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
